import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useDarkMode } from "../contexts/DarkModeContext";
import Navbar from "./Navbar";
import Footer from "./Footer";

// Floating Dark Mode Toggle Component
function FloatingDarkModeToggle() {
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={toggleDarkMode}
      className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200"
      aria-label="Toggle dark mode"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3, delay: 1 }}
    >
      {isDarkMode ? (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </motion.button>
  );
}

const SupportContact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    issueType: "",
    lookingFor: [],
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = "error") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleScrollToSection = (sectionId) => {
    navigate(`/#${sectionId}`);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      // Handle checkbox for lookingFor array
      setFormData((prev) => ({
        ...prev,
        [name]: checked
          ? [...prev[name], value]
          : prev[name].filter((item) => item !== value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email via Edge Function
      const { error: emailError } = await supabase.functions.invoke(
        "send-support-email",
        {
          body: {
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            issueType: formData.issueType,
            lookingFor: formData.lookingFor.join(", "),
            message: formData.message,
          },
        }
      );

      if (emailError) {
        console.error("Email sending error:", emailError);
        showNotification("Failed to send message. Please try again.", "error");
        return;
      }

      // Success
      setIsSubmitted(true);
    } catch (error) {
      console.error("Submission error:", error);
      showNotification("Failed to send message. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
        <Navbar scrollToSection={handleScrollToSection} activeSection="" />
        <div className="flex items-center justify-center pt-16 pb-8 min-h-screen">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          >
            <div className="bg-green-100 dark:bg-green-900/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Message Sent!
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              Thank you for contacting us. We'll get back to you as soon as
              possible.
            </p>
            <motion.a
              href="/"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-aifund text-white px-8 py-3 rounded-full font-semibold hover:bg-aifund-dark transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Back to Home
            </motion.a>
          </motion.div>
        </div>
        
        {/* Footer */}
        <Footer />
        
        {/* Floating Dark Mode Toggle */}
        <FloatingDarkModeToggle />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Navbar scrollToSection={handleScrollToSection} activeSection="" />
      <div className="pt-16 pb-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Contact Support
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Have a question or need help? We're here to assist you with
              anything related to the Buildathon.
            </p>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  placeholder="Your Full Name"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Subject */}
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                >
                  <option value="">Select a subject</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Event Information">Event Information</option>
                  <option value="Registration Help">Registration Help</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              {/* Looking for team members - conditional field */}
              {formData.subject === "Looking for team members" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    What are you looking for? (Select all that apply)
                  </label>
                  <div className="space-y-3">
                    {[
                      "AI Engineer",
                      "Software Engineer",
                      "Data Scientist",
                      "Machine Learning Engineer",
                      "Product Manager",
                      "UX/UI Designer",
                      "DevOps Engineer",
                      "Full Stack Developer",
                      "Frontend Developer",
                      "Backend Developer",
                    ].map((role) => (
                      <label key={role} className="flex items-center">
                        <input
                          type="checkbox"
                          name="lookingFor"
                          value={role}
                          checked={formData.lookingFor.includes(role)}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-aifund border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded focus:ring-aifund"
                        />
                        <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                          {role}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Technical Support - conditional field */}
              {formData.subject === "Technical Support" && (
                <div>
                  <label
                    htmlFor="issueType"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    What type of technical issue are you experiencing?
                  </label>
                  <select
                    id="issueType"
                    name="issueType"
                    value={formData.issueType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  >
                    <option value="">Select issue type</option>
                    <option value="Registration Problems">
                      Registration Problems
                    </option>
                    <option value="Website Issues">Website Issues</option>
                    <option value="Account Access">Account Access</option>
                    <option value="Upload Problems">Upload Problems</option>
                    <option value="Other Technical Issue">
                      Other Technical Issue
                    </option>
                  </select>
                </div>
              )}

              {/* Event Information - conditional field */}
              {formData.subject === "Event Information" && (
                <div>
                  <label
                    htmlFor="issueType"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    What information do you need?
                  </label>
                  <select
                    id="issueType"
                    name="issueType"
                    value={formData.issueType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  >
                    <option value="">Select information type</option>
                    <option value="Schedule Details">Schedule Details</option>
                    <option value="Location & Directions">
                      Location & Directions
                    </option>
                    <option value="What to Bring">What to Bring</option>
                    <option value="Food & Accommodations">
                      Food & Accommodations
                    </option>
                    <option value="Prizes & Judging">Prizes & Judging</option>
                    <option value="Other Event Info">Other Event Info</option>
                  </select>
                </div>
              )}

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-aifund focus:border-transparent transition-colors duration-300"
                  placeholder="Please describe your question or issue in detail..."
                />
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                  className={`px-8 py-3 rounded-full font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl ${
                    isSubmitting
                      ? "bg-gray-400 text-gray-500 cursor-not-allowed"
                      : "bg-aifund text-white hover:bg-aifund-dark"
                  }`}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </motion.button>
              </div>
            </form>
          </motion.div>

          {/* Notification */}
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
                notification.type === "success"
                  ? "bg-green-100 text-green-800 border-l-4 border-green-500"
                  : "bg-red-100 text-red-800 border-l-4 border-red-500"
              }`}
            >
              {notification.message}
            </motion.div>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
      
      {/* Floating Dark Mode Toggle */}
      <FloatingDarkModeToggle />
    </div>
  );
};

export default SupportContact;
